import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, List, LogOut, Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import { api, errorText, setToken } from "@/lib/api";
import { cn, initials } from "@/lib/utils";
import { AGENT_EVENT } from "@/hooks/useAgent";
import { AgentLayout } from "@/components/agent/AgentLayout";
import { Button } from "@/components/ui/button";
import { SettingsCard, SettingsControlRow } from "@/components/ui/settings-card";
import { DangerButton, Field, PageHeader, PrimaryButton, errorCls, inputCls } from "@/components/agent/ui";

const Segmented = ({ value, options, onChange, testid }) => (
  <div className="flex items-center gap-0.5 rounded-full border p-0.5" role="radiogroup" data-testid={testid}>
    {options.map(({ value: v, label, icon: Icon }) => (
      <Button key={v} variant="ghost" size="sm" role="radio" aria-checked={value === v} onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={cn("rounded-full px-2.5", value === v ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/60 hover:text-foreground")}>
        <Icon className="size-3.5" /> {label}
      </Button>
    ))}
  </div>
);

export default function Settings() {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [me, setMe] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("landin-default-property-view") || "cards");

  useEffect(() => {
    api.get("/auth/me").then((r) => { setMe(r.data); setName(r.data.name || ""); }).catch((err) => setError(errorText(err)));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch("/auth/me", { name: name.trim() });
      setMe(data);
      window.dispatchEvent(new Event(AGENT_EVENT));
      toast.success("Name saved");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const signOut = () => {
    setToken(null);
    navigate("/login", { replace: true });
  };
  const pickView = (v) => {
    setView(v);
    localStorage.setItem("landin-default-property-view", v);
    sessionStorage.removeItem("landin-property-view");
  };

  return (
    <AgentLayout>
      <PageHeader title="Account" description="Your profile and how Landin looks for you." />
      <SettingsCard title="Profile" id="profile" testid="settings-profile">
        <div className="flex items-center gap-3">
          <span className="grid size-12 shrink-0 place-items-center rounded-full bg-foreground text-sm font-medium text-background">{initials(me?.name || me?.email || "")}</span>
          <div className="min-w-0 flex-1">
            <p className="text-base font-medium [overflow-wrap:anywhere]">{me?.name}</p>
            <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{me?.email}</p>
          </div>
          <DangerButton onClick={signOut} testid="settings-sign-out"><LogOut /> Sign out</DangerButton>
        </div>
        <form onSubmit={save} className="flex flex-col gap-4 border-t pt-4" data-testid="settings-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Your name">
              <input maxLength={120} value={name} onChange={(e) => setName(e.target.value)} className={inputCls} data-testid="settings-name" />
            </Field>
            <Field label="Email" help="Used to sign in.">
              <input value={me?.email || ""} readOnly disabled className={inputCls} data-testid="settings-email" />
            </Field>
          </div>
          {error && <p className={errorCls} data-testid="settings-error">{error}</p>}
          <div className="flex justify-end">
            <PrimaryButton type="submit" isLoading={busy} disabled={!name.trim() || name.trim() === (me?.name || "")} testid="settings-save-name">Save</PrimaryButton>
          </div>
        </form>
      </SettingsCard>
      <SettingsCard title="Appearance" id="appearance" testid="settings-appearance">
        <SettingsControlRow label="Theme" description="System follows your device's light or dark setting."
          control={<Segmented value={theme} onChange={setTheme} testid="theme" options={[{ value: "light", label: "Light", icon: Sun }, { value: "dark", label: "Dark", icon: Moon }, { value: "system", label: "System", icon: Monitor }]} />} />
      </SettingsCard>
      <SettingsCard title="Preferences" description="Saved on this device." id="preferences" testid="settings-preferences">
        <SettingsControlRow label="Properties view" description="How the Properties page opens."
          control={<Segmented value={view} onChange={pickView} testid="default-view" options={[{ value: "cards", label: "Cards", icon: LayoutGrid }, { value: "list", label: "List", icon: List }]} />} />
      </SettingsCard>
    </AgentLayout>
  );
}
